/**
 * Reads an article PDF and finds its dates, as printed on the first page, e.g.
 *   "Article Received: 10/01/2026 | Accepted: 26/04/2026"
 * Also understands "Revised: …", "Resubmitted: …", "Final acceptance: …", "Published online: 1 July 2026"
 * and month names.
 * Dates are returned as YYYY-MM-DD (for <input type="date">); missing ones are ''.
 * Numeric dates are read as DD/MM/YYYY, as printed in AJHM PDFs.
 */

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

const pad = (n) => String(n).padStart(2, '0');

const toIso = (day, month, year) => {
  const d = Number(day);
  const m = Number(month);
  const y = Number(year);
  if (!(d >= 1 && d <= 31 && m >= 1 && m <= 12 && y >= 1990 && y <= 2100)) return '';
  return `${y}-${pad(m)}-${pad(d)}`;
};

// "26/04/2026", "26-04-2026", "26.04.2026", "26 April 2026", "26th April, 2026", "April 26, 2026"
const DATE = String.raw`(\d{1,2})\s*[/.-]\s*(\d{1,2})\s*[/.-]\s*(\d{4})|(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]{3,9})\.?,?\s+(\d{4})|([A-Za-z]{3,9})\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})`;

const parseMatch = (m) => {
  if (!m) return '';
  if (m[1]) return toIso(m[1], m[2], m[3]);
  if (m[4]) return toIso(m[4], MONTHS.indexOf(m[5].slice(0, 3).toLowerCase()) + 1, m[6]);
  if (m[7]) return toIso(m[8], MONTHS.indexOf(m[7].slice(0, 3).toLowerCase()) + 1, m[9]);
  return '';
};

const findDate = (text, labels) => {
  const re = new RegExp(String.raw`(?:${labels})\s*(?:on|date)?\s*[:\-–]?\s*(?:${DATE})`, 'i');
  const m = text.match(re);
  return parseMatch(m);
};

/** Finds the dates in already-extracted PDF text. */
export const datesFromText = (rawText) => {
  // PDFs often split digits with spaces ("1 0 / 0 1 / 202 6"), so join digit runs first
  const text = String(rawText || '')
    .replace(/\s+/g, ' ')
    .replace(/(\d) (?=\d)/g, '$1')
    .replace(/(\d) ?([/.-]) ?(?=\d)/g, '$1$2');
  return {
    receivedDate: findDate(text, '(?<!revis(?:ed|ion)\\s)received'),
    revisedDate: findDate(text, 'resubmitted|re-submitted|resubmission|revised(?:\\s+manuscript)?(?:\\s+received)?|revision\\s+received'),
    acceptedDate: findDate(text, '(?<!final\\s)(?<!finally\\s)accepted'),
    finalAcceptedDate: findDate(text, 'final\\s+acceptance|finally\\s+accepted|final\\s+accepted'),
    publishedDate: findDate(text, 'published online|published|publication date|date of publication'),
  };
};

let pdfjsPromise = null;
const loadPdfjs = () => {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist/legacy/build/pdf').then((pdfjs) => {
      pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;
      return pdfjs;
    });
  }
  return pdfjsPromise;
};

/** Reads the first pages of a PDF (File, Blob or ArrayBuffer) and returns its dates and its page count. */
export async function readDatesFromPdf(source, maxPages = 2) {
  const pdfjs = await loadPdfjs();
  const data = source instanceof ArrayBuffer ? source : await source.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: new Uint8Array(data), verbosity: 0 }).promise;
  let text = '';
  for (let i = 1; i <= Math.min(doc.numPages, maxPages); i++) {
    const content = await (await doc.getPage(i)).getTextContent();
    text += ` ${content.items.map((item) => item.str).join(' ')}`;
  }
  return { ...datesFromText(text), pageCount: doc.numPages };
}

/** Downloads a PDF by URL (tries each URL in turn) and returns its dates. */
export async function readDatesFromPdfUrl(urls) {
  let lastError = null;
  for (const url of urls.filter(Boolean)) {
    try {
      const res = await fetch(url);
      const type = res.headers.get('content-type') || '';
      if (!res.ok || !type.includes('pdf')) throw new Error(`Could not download the PDF (${res.status})`);
      return await readDatesFromPdf(await res.arrayBuffer());
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError || new Error('This article has no PDF.');
}

/** "2026-04-26" -> "26 April 2026" */
export const formatDisplayDate = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '';
  const names = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return `${Number(m[3])} ${names[Number(m[2]) - 1]} ${m[1]}`;
};
